import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue';
import moment, { Moment } from 'moment';
import {
    DeliveryDateGroup,
    DeliveryListTableItem,
    DELIVERY_LIST_TABLE_COLUMNS,
    filterDeliveryDateGroups,
} from '@/modules/Logistics/helpers/LogisticsTableHelper';
import type { DeliveryTablePagination } from '@/modules/Logistics/interfaces/LogisticsInterfaces';
import { downloadTableAsExcel } from '@/helpers/PrintHelper';
import { getDatepickerLocale } from '@/helpers/LocaleHelper';

// Props

export default /*#__PURE__*/_defineComponent({
  __name: 'DeliveryTable',
  props: {
    dataSource: null,
    searchQuery: null,
    loading: { type: Boolean },
    pagination: null
  },
  emits: ["updateSearch", "row-click", "delete", "downloadPDF", "pagination-change"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'updateSearch', value: Moment | null): void;
    (e: 'row-click', record: DeliveryDateGroup | DeliveryListTableItem): void;
    (e: 'delete', records: DeliveryListTableItem[], onConfirm: () => void): void;
    (e: 'downloadPDF', record: DeliveryListTableItem): void;
    (e: 'pagination-change', current: number, pageSize: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    dataSource: DeliveryDateGroup[];
    searchQuery: Moment | null;
    loading: boolean;
    pagination: DeliveryTablePagination;
};



// Emits


// State
const selectedRowKeys = ref<Array<string | number>>([]);
const locale = computed(() => getDatepickerLocale());

// Table columns
const columns = computed(() => {
    return DELIVERY_LIST_TABLE_COLUMNS();
});

// Computed
const filteredData = computed(() => {
    return filterDeliveryDateGroups(props.dataSource, props.searchQuery ? props.searchQuery.format('DD.MM.YYYY') : '');
});

const expandedRowKeys = computed(() => filteredData.value.map((group) => group.id));

// Row selection config
const rowSelection = computed(() => ({
    selectedRowKeys: selectedRowKeys.value,
    onChange: (keys: Array<string | number>) => {
        handleSelectionChange(keys);
    },
}));

// Methods
function handleSearchChange(date: Moment | null) {
    emit('updateSearch', date);
}

function handleTableChange(pagination: { current: number; pageSize: number }) {
    if (pagination.current === props.pagination.current && pagination.pageSize === props.pagination.pageSize) {
        return;
    }

    emit('pagination-change', pagination.current, pagination.pageSize);
}

/**
 * Keep date-group selection in sync with its children:
 * selecting a date row selects all of its delivery lists;
 * deselecting removes its delivery lists.
 */
function handleSelectionChange(keys: Array<string | number>) {
    const previous = new Set(selectedRowKeys.value);
    const next = new Set(keys);

    for (const group of filteredData.value) {
        const wasSelected = previous.has(group.id);
        const isSelected = next.has(group.id);

        if (isSelected && !wasSelected) {
            group.children.forEach((child) => next.add(child.id));
        } else if (!isSelected && wasSelected) {
            group.children.forEach((child) => next.delete(child.id));
        }
    }

    selectedRowKeys.value = Array.from(next);
}

function customRow(record: DeliveryDateGroup | DeliveryListTableItem) {
    if (record.isDateRow) {
        return {
            style: {
                cursor: 'default',
            },
        };
    }

    return {
        on: {
            click: () => emit('row-click', record),
        },
        style: {
            cursor: 'pointer',
        },
    };
}

function handleDeleteSelected() {
    const selectedKeys = new Set(selectedRowKeys.value);
    const records = props.dataSource.flatMap((group) => group.children.filter((record) => selectedKeys.has(record.id)));

    emit('delete', records, () => (selectedRowKeys.value = []));
}

function handleDownloadExcel() {
    const exportColumns = columns.value.filter((col) => 'dataIndex' in col && col.dataIndex);
    downloadTableAsExcel(exportColumns as any, filteredData.value, 'delivery-lists');
}

function rowClassName(record: DeliveryDateGroup | DeliveryListTableItem) {
    const today = moment().format('DD.MM.YYYY');
    const hierarchyClass = record.isDateRow ? 'delivery-table__parent-row' : 'delivery-table__child-row';
    return record.date === today ? `${hierarchyClass} today-row` : hierarchyClass;
}

return { __sfc: true,props, emit, selectedRowKeys, locale, columns, filteredData, expandedRowKeys, rowSelection, handleSearchChange, handleTableChange, handleSelectionChange, customRow, handleDeleteSelected, handleDownloadExcel, rowClassName }
}

})